/**
 * Licensed Materials - Property of IBM
 *
 * "Restricted Materials of IBM"
 *
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { Component } from 'react';
import * as mobx from 'mobx';

import PropTypes from 'util/PropTypes';
import { observer } from 'mobx-react';
import { types } from 'mobx-state-tree';

import FloatingToolbar from 'components/floatingtoolbar/FloatingToolbar';
import { eventIsInFloatingToolbar } from 'components/floatingtoolbar/FloatingToolbar';
import Flyout from 'components/flyout/Flyout';
import FieldSearchInput from './FieldSearchInput';
import StringResources from '../../../nls/StringResources';

import delete32 from '@ba/ba-graphics/dist/icons-js/delete_32';

import makeCenter16 from '@ba/ba-graphics/dist/icons-js/make-center_16.js';
import drawLink16 from '@ba/ba-graphics/dist/icons-js/draw-link_16.js';
import './RelationshipWidgetContextMenu.scss';

const QUERY_ITEM = 'QueryItem';

function setTimeoutPromise(effect, delay) {
	let cancel, handle;
	const promise = new Promise((resolve, reject) => {
		cancel = reject;
		handle = setTimeout(resolve, delay);
	}).then(
		effect,
		() => {
			clearTimeout(handle);
			throw new Error('Context menu cancelled');
		}
	);

	return {
		promise, cancel
	};
}


const DELAY = 250;

const model = types
	.model('RelationshipWidgetContextMenuViewModel', {
		setTargetHandle: types.optional(types.union(types.number, types.null), null)
	})
	.volatile(() => ({
		target: null,
		_setTargetPromiseInfo: null
	}))
	.extend(self => {

		let promiseInfo = null;

		return {
			views: {
				getTargetPromise() {
					return promiseInfo && promiseInfo.promise;
				}
			},
			actions: {
				setContextMenuTarget(target) {
					//Upon setting a new target, immediately clear the old target
					if(promiseInfo) {
						promiseInfo.cancel();
						promiseInfo = null;
					}
					self.target = null;

					//If there is a new target, initiate a timer to show it
					if(target) {
						promiseInfo = setTimeoutPromise(
							() => {
								self._setTarget(target);
								promiseInfo = null;
							},
							DELAY
						);
					}
				},
				_setTarget(target) {
					self.target = target;
				}
			}
		};
	});


@observer
class RelationshipWidgetContextMenu extends Component {
	constructor(props) {
		super(props);
		this.model = model.create({});

		this.disposeFunctions = [];
		this.disposeFunctions.push(
			mobx.autorun(() => {
				const relationshipVM = this.props.startingPointsVM.relationship;
				if(relationshipVM.contextMenuElement) {
					this.model.setContextMenuTarget(relationshipVM.contextMenuElement);
				} else {
					this.model.setContextMenuTarget(null);
					//when context menu close, the flyout of the context menu button should also be closed
					this.setState({
						showFieldSearchInput: {
							shown: false,
							triggerNode: null
						}
					});
				}
			})
		);
		this.state = {
			showFieldSearchInput: {
				shown: false,
				triggerNode: null
			}
		};
	}

	render() {
		const startingPointsVM = this.props.startingPointsVM;
		const fieldsVM = startingPointsVM.fields;
		const relationshipVM = startingPointsVM.relationship;

		if(this.model.target) {
			return <FloatingToolbar
				buttons={[
					{
						icon: makeCenter16.id,
						label: StringResources.get('startingPoints_relationshipDiagram_selectNewFocusField'),
						action: () => this.setFocusField(relationshipVM.contextMenuField),
						include: () => fieldsVM.canSetFocusField(relationshipVM.contextMenuField.itemId)
					},
					{
						icon: drawLink16.id,
						label: StringResources.get('startingPoints_relationshipDiagram_show_relationships_between_two_fields'),
						action: (event) => this.toggleFieldSearchInput(event),
						include: () => relationshipVM.canShowRelationships(),
						otherComponents: () => this._renderShowRelationshipsFieldSearchInput()
					},
					{
						icon: delete32.id,
						label: StringResources.get('startingPoints_relationshipDiagram_deleteField'),
						action: () => this.deleteFields([relationshipVM.contextMenuField]),
						include: () => relationshipVM.canDeleteFields([relationshipVM.contextMenuField])
					}
				]}
				triggerNode={this.model.target}
			/>;
		} else {
			return null;
		}
	}

	dismissContextMenu() {
		const startingPointsVM = this.props.startingPointsVM;
		const relationshipVM = startingPointsVM.relationship;
		relationshipVM.setContextMenu({ element: null, field: null });
	}

	deleteFields(fields) {
		const startingPointsVM = this.props.startingPointsVM;
		const relationshipVM = startingPointsVM.relationship;
		relationshipVM.deleteFields(fields);
		this.dismissContextMenu();
	}

	setFocusField(field) {
		const startingPointsVM = this.props.startingPointsVM;
		const fieldsVM = startingPointsVM.fields;
		fieldsVM.setFocusField(field.itemId);
		this.dismissContextMenu();
	}

	bodyOnMouseDown(evt) {
		if(!eventIsInFloatingToolbar(evt)) {
			this.dismissContextMenu();
		}
	}

	toggleFieldSearchInput(event) {
		if (this.state.showFieldSearchInput.shown) {
			this.setState({
				showFieldSearchInput: {
					shown: false,
					triggerNode: null
				}
			});
		} else {
			if(event.target) {
				this.setState({
					showFieldSearchInput: {
						shown: true,
						triggerNode: event.target
					}
				});
			}
		}
	}

	_renderShowRelationshipsFieldSearchInput() {
		if (this.state.showFieldSearchInput.shown) {
			return <Flyout
				className={'showRelationshipsBetweenTwoFields__fieldSearchInputFlyout'}
				triggerNode={this.state.showFieldSearchInput.triggerNode}
			>
				<FieldSearchInput
					module={this.props.module}
					placeholder={StringResources.get('startingPoints_relationshipDiagram_show_relationships_between_two_fields_search_input_placeholder')}
					onSelect={this._onShowRelationships.bind(this)}
				/>
			</Flyout>;
		} else {
			return null;
		}
	}

	_onShowRelationships(item) {
		if(!item) {
			return;
		}

		const startingPointsVM = this.props.startingPointsVM;
		const relationshipVM = startingPointsVM.relationship;
		const queryObjectType = item.getObjectType();
		const showRelationshipFrom = relationshipVM.contextMenuField.itemId;
		const showRelationshipTo = item.getIdForExpression();
		if (
			queryObjectType === QUERY_ITEM &&
			showRelationshipTo !== showRelationshipFrom
		) {
			//can only select a field, can not select table, cannot select target field and current context menu field
			//do showing relationships
			const targetModel = relationshipVM.getTargetModel();
			targetModel.showRelationshipsBetween(showRelationshipFrom, showRelationshipTo);
			//dismiss context menu
			this.dismissContextMenu();
		}
	}

	componentDidMount() {
		this._bodyOnMouseDownHandler = this.bodyOnMouseDown.bind(this);
		document.body.addEventListener('mousedown', this._bodyOnMouseDownHandler);
	}

	componentWillUnmount() {
		this.disposeFunctions.forEach(dispose => dispose());
		this.disposeFunctions = null;
		if(this._bodyOnMouseDownHandler) {
			window.removeEventListener('mousedown', this._bodyOnMouseDownHandler);
			this._bodyOnMouseDownHandler = null;
		}
	}
}

RelationshipWidgetContextMenu.propTypes = {
	module: PropTypes.observableObject.isRequired,
	startingPointsVM: PropTypes.object.isRequired
};

export default RelationshipWidgetContextMenu;



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/relationshipGraph/RelationshipWidgetContextMenu.js